import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axios } from "../../config";
import { getRequest, postRequest } from "../../Apis/apiWrapper";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_SUCCESS, EMITTER_ERROR } from "../../utils/constants";
import { qaEndpoints } from "../../utils/constants/httpConstants";
import { captureQaContext } from "../../utils/qa/qaContext";
import { MAX_SHOTS, imageFileToShot, imageFilesFrom, dragCarriesFiles } from "../../utils/qa/qaCapture";
import { Button } from "../../components/Buttons";
// Imported from the concrete files rather than the InputFields barrel: that barrel
// re-exports a non-existent "./Input" module, so the app itself pulls these in by file.
import InputField from "../../components/InputFields/InputField";
import TextArea from "../../components/InputFields/TextArea";
import { AutoCompleteCustom } from "../../components/Autocomplete/AutoCompleteCustom";
import StatusChip from "../../components/StatusChip/StatusChip";
import TypographyInline from "../../components/Typography/TypographyInline";
import IconComponent from "../../components/Icon/IconComponent";
import { GREY, BASE, SUCCESS } from "../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../utils/constants/UI";
import { fontSize } from "../../utils/constants/UI/uiConstants";
import QaPortalModal from "./QaPortalModal";
import AnnotateShot from "./AnnotateShot";
import { styles, STATUS_META, eventLabel, describeEvent } from "./qaStyles";

const EMPTY_SPEC_FORM = {
  title: "", kind: "bug", severity: "", severity_reason: "", area: "",
  expected: "", actual: "", repro: "", acceptance: "", fix_direction: "", impact: "",
};

const KIND_ITEMS = [
  { id: "bug", label: "Bug" },
  { id: "feature", label: "Feature" },
];
const SEVERITY_ITEMS = [
  { id: "", label: "-" },
  { id: "p0", label: "p0 - critical" },
  { id: "p1", label: "p1 - high" },
  { id: "p2", label: "p2 - medium" },
  { id: "p3", label: "p3 - low" },
];

const seedSpecForm = (spec, fallbackKind) => ({
  title: spec?.title || "",
  kind: spec?.kind || fallbackKind || "bug",
  severity: spec?.severity || "",
  severity_reason: spec?.severity_reason || "",
  area: spec?.area || "",
  expected: spec?.expected || "",
  actual: spec?.actual || "",
  repro: Array.isArray(spec?.repro_steps) ? spec.repro_steps.join("\n") : "",
  acceptance: Array.isArray(spec?.acceptance_criteria) ? spec.acceptance_criteria.join("\n") : "",
  fix_direction: spec?.fix_direction || "",
  impact: spec?.impact || "",
});

const splitLines = (text) => String(text || "").split("\n").map((line) => line.trim()).filter(Boolean);

const formatDate = (value) => {
  try {
    return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return "";
  }
};

// Small label above a field - replaces the old styled <label>. Keeps the 12px / 600
// look the form used, now from the design-system Typography primitive.
const FieldLabel = ({ label }) => (
  <div style={styles.fieldBlock}>
    <TypographyInline size="xs" color={GREY.QUATINARY} label={label} fontWeight={fontWeight.SEMIBOLD} />
  </div>
);

const NoteText = ({ label, color = GREY.TERTIARY, style }) => (
  <div style={{ marginTop: "8px", ...(style || {}) }}>
    <TypographyInline size="xs" color={color} label={label} fontWeight={fontWeight.REGULAR} />
  </div>
);

const ReportModal = ({
  open,
  onClose,
  draft,
  setDraft,
  nativeSupported,
  onNativeCapture,
  onStash,
  aiEnabled,
  refreshBadge,
  initialStep,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("compose");
  const [context, setContext] = useState(null);
  const [dragDepth, setDragDepth] = useState(0);
  const [annotateIndex, setAnnotateIndex] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [filed, setFiled] = useState(null);
  const [specForm, setSpecForm] = useState(EMPTY_SPEC_FORM);
  const [aiSeeded, setAiSeeded] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [assistBusy, setAssistBusy] = useState(false);
  const [assistSeconds, setAssistSeconds] = useState(0);
  const [clarify, setClarify] = useState({ question: "", answer: "" });
  const [myIssues, setMyIssues] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailShots, setDetailShots] = useState([]);
  const [shotsLoading, setShotsLoading] = useState(false);
  const [shotCount, setShotCount] = useState(0);
  const [verifyNote, setVerifyNote] = useState("");
  const [verifyMode, setVerifyMode] = useState(null); // null | 'no'
  const [comment, setComment] = useState("");
  const fileInputRef = useRef(null);
  const detailShotsRef = useRef([]);

  const shots = draft.shots || [];

  // Context is captured when the dialog opens (not on a timer): the dialog itself
  // doesn't change <main>, so section/tab reads stay correct.
  useEffect(() => {
    if (open) {
      setContext(captureQaContext());
      if (initialStep) setStep(initialStep);
    }
  }, [open, initialStep]);

  const resetAll = useCallback(() => {
    setStep("compose");
    setDraft({ kind: "bug", description: "", shots: [] });
    setSpecForm(EMPTY_SPEC_FORM);
    setAiSeeded(false);
    setDuplicates([]);
    setClarify({ question: "", answer: "" });
    setFiled(null);
    setVerifyMode(null);
    setVerifyNote("");
    setComment("");
  }, [setDraft]);

  const appendShots = useCallback(async (files) => {
    const room = MAX_SHOTS - (draft.shots || []).length;
    if (room <= 0) {
      EventEmitter.emit(EMITTER_ERROR, `Up to ${MAX_SHOTS} screenshots per report.`);
      return;
    }
    const next = [];
    for (const file of files.slice(0, room)) {
      const shot = await imageFileToShot(file);
      if (shot) next.push(shot);
    }
    if (next.length) setDraft((prev) => ({ ...prev, shots: [...(prev.shots || []), ...next] }));
  }, [draft.shots, setDraft]);

  // Window-level drag/drop + paste while composing. The window handlers ALWAYS
  // preventDefault so a drop outside the box can never navigate the tab away, and
  // there are NO box-local drop handlers (one drop -> exactly one ingest). The paste
  // handler acts only when the clipboard carries image FILES - text pastes pass through.
  useEffect(() => {
    if (!open || step !== "compose") return undefined;
    const onDragOver = (event) => event.preventDefault();
    const onDrop = (event) => {
      event.preventDefault();
      setDragDepth(0);
      const files = imageFilesFrom(event.dataTransfer);
      if (files.length) appendShots(files);
    };
    const onDragEnter = (event) => {
      if (dragCarriesFiles(event)) setDragDepth((d) => d + 1);
    };
    const onDragLeave = (event) => {
      if (dragCarriesFiles(event)) setDragDepth((d) => Math.max(0, d - 1));
    };
    const onPaste = (event) => {
      const files = imageFilesFrom(event.clipboardData);
      if (files.length) {
        event.preventDefault();
        appendShots(files);
      }
    };
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    document.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("paste", onPaste);
    };
  }, [open, step, appendShots]);

  useEffect(() => {
    if (!assistBusy) return undefined;
    setAssistSeconds(0);
    const timer = setInterval(() => setAssistSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [assistBusy]);

  const runPolish = async (answer) => {
    setAssistBusy(true);
    try {
      const payload = {
        description: draft.description,
        kind: draft.kind,
        screenshots: shots.slice(0, 3),
        context,
      };
      if (answer) {
        payload.clarification_question = clarify.question;
        payload.clarification_answer = answer;
      }
      const res = await postRequest(navigate, qaEndpoints.assist, payload);
      const data = res?.data?.data || {};
      if (data.clarifying_question && !answer) {
        setClarify({ question: data.clarifying_question, answer: "" });
        setStep("clarify");
      } else if (data.spec) {
        setSpecForm(seedSpecForm(data.spec, draft.kind));
        setDuplicates(Array.isArray(data.spec.possible_duplicates) ? data.spec.possible_duplicates : []);
        setAiSeeded(true);
        setStep("review");
      } else {
        EventEmitter.emit(EMITTER_ERROR, "AI polish returned nothing useful - you can still file as written.");
      }
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "AI polish failed. You can still file the report as written.");
    } finally {
      setAssistBusy(false);
    }
  };

  const fileIssue = async ({ withSpec, polishAsync }) => {
    setFileBusy(true);
    try {
      const payload = {
        kind: withSpec ? specForm.kind : draft.kind,
        description: draft.description,
        screenshots: shots,
        context,
        route: context?.route || "",
        polish_async: !!polishAsync,
      };
      if (withSpec) {
        payload.title = specForm.title;
        payload.ai_assisted = aiSeeded;
        payload.spec = {
          title: specForm.title,
          kind: specForm.kind,
          severity: specForm.severity || null,
          severity_reason: specForm.severity_reason,
          area: specForm.area,
          expected: specForm.expected,
          actual: specForm.actual,
          repro_steps: splitLines(specForm.repro).slice(0, 5),
          acceptance_criteria: splitLines(specForm.acceptance).slice(0, 3),
          fix_direction: specForm.fix_direction,
          impact: specForm.impact,
          possible_duplicates: duplicates,
        };
      }
      const res = await postRequest(navigate, qaEndpoints.issues, payload);
      setFiled(res?.data?.data || {});
      setStep("done");
      EventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || "Report filed. Thank you!");
      refreshBadge?.();
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "Could not file the report. Please try again.");
    } finally {
      setFileBusy(false);
    }
  };

  const loadMyIssues = useCallback(async () => {
    try {
      const res = await getRequest(navigate, qaEndpoints.issues);
      setMyIssues(res?.data?.data?.issues || []);
    } catch (err) {
      setMyIssues([]);
    }
  }, [navigate]);

  const cleanupDetailShots = () => {
    detailShotsRef.current.forEach((url) => URL.revokeObjectURL(url));
    detailShotsRef.current = [];
  };

  const openDetail = async (id) => {
    setStep("detail");
    setDetail(null);
    setVerifyMode(null);
    setVerifyNote("");
    cleanupDetailShots();
    setDetailShots([]);
    try {
      const res = await getRequest(navigate, qaEndpoints.issue(id));
      const data = res?.data?.data || {};
      setDetail(data);
      const count = data.issue?.screenshot_count || 0;
      setShotCount(count);
      if (count === 0) {
        setShotsLoading(false);
        setDetailShots([]);
        detailShotsRef.current = [];
        return;
      }
      // Show placeholders immediately, then fetch all shots in PARALLEL. Promise.all
      // preserves index order regardless of which blob arrives first, so a slow shot
      // never blocks the rest and the drawer never looks empty/broken while loading.
      setShotsLoading(true);
      const results = await Promise.all(
        Array.from({ length: count }, (_, i) =>
          axios
            .get(qaEndpoints.screenshot(id, i), { responseType: "blob" })
            .then((blob) => URL.createObjectURL(blob.data))
            .catch(() => null)
        )
      );
      const urls = results.filter(Boolean);
      detailShotsRef.current = urls;
      setDetailShots(urls);
      setShotsLoading(false);
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "Could not load the report.");
      setStep("list");
    }
  };

  useEffect(() => () => cleanupDetailShots(), []);

  const submitVerify = async (ok) => {
    if (!ok && verifyNote.trim().length < 3) {
      EventEmitter.emit(EMITTER_ERROR, "Please describe what is still broken.");
      return;
    }
    try {
      const res = await postRequest(navigate, qaEndpoints.verify(detail.issue._id), { ok, note: verifyNote });
      EventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || "Thanks!");
      refreshBadge?.();
      openDetail(detail.issue._id);
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "Could not record your confirmation.");
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await postRequest(navigate, qaEndpoints.events(detail.issue._id), { body: comment.trim() });
      setComment("");
      openDetail(detail.issue._id);
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "Could not add the comment.");
    }
  };

  const plusOneDuplicate = async (id) => {
    try {
      const res = await postRequest(navigate, qaEndpoints.plusone(id), {});
      EventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || "Noted - thanks!");
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || "Could not +1 that issue.");
    }
  };

  const canFile = (draft.description || "").trim().length >= 3 && !fileBusy;

  // AutoCompleteCustom shows the selected LABEL (not the id), so map the spec form's
  // stored id back to its item label for the value display. Empty array = placeholder.
  const kindValue = KIND_ITEMS.filter((i) => i.id === specForm.kind).map((i) => i.label);
  const severityValue = SEVERITY_ITEMS
    .filter((i) => i.id === specForm.severity && i.id !== "")
    .map((i) => i.label);

  const renderShotsStrip = () => (
    <div style={styles.shotsStrip}>
      {shots.map((shot, index) => (
        <div key={index} style={styles.shotThumbWrap} onClick={() => setAnnotateIndex(index)} title="Annotate">
          <img src={shot} alt={`screenshot ${index + 1}`} style={styles.shotThumb} />
          <button
            type="button"
            style={styles.shotRemove}
            title="Remove"
            onClick={(event) => {
              event.stopPropagation();
              setDraft((prev) => ({ ...prev, shots: prev.shots.filter((_, i) => i !== index) }));
            }}
          >
            &times;
          </button>
          <div style={styles.shotAnnotateHint}>annotate</div>
        </div>
      ))}
    </div>
  );

  const renderCompose = () => (
    <>
      <div style={styles.dialogBody}>
        {/* Segmented kind toggle - DS Buttons (active = primary coral, idle = ghost). */}
        <div style={styles.kindToggle}>
          <div style={styles.inlineBtn}>
            <Button
              label="Bug"
              size="sm36"
              variant={draft.kind === "bug" ? "primary" : "ghost"}
              onClick={() => setDraft((p) => ({ ...p, kind: "bug" }))}
            />
          </div>
          <div style={styles.inlineBtn}>
            <Button
              label="Feature request"
              size="sm36"
              variant={draft.kind === "feature" ? "primary" : "ghost"}
              onClick={() => setDraft((p) => ({ ...p, kind: "feature" }))}
            />
          </div>
        </div>
        <FieldLabel label="What happened? (your own words)" />
        <TextArea
          isResize
          autoFocus
          rows={4}
          placeholder={draft.kind === "bug" ? "What did you do, what did you expect, what happened instead?" : "What would you like ReachIQ to do?"}
          value={draft.description}
          onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
        />
        <FieldLabel label={`Screenshots (${shots.length}/${MAX_SHOTS})`} />
        {shots.length > 0 && renderShotsStrip()}
        <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
          {nativeSupported && (
            <div style={styles.inlineBtn}>
              <Button label="Capture screen" size="sm36" variant="ghost" disabled={shots.length >= MAX_SHOTS} onClick={onNativeCapture} />
            </div>
          )}
          <div style={styles.inlineBtn}>
            <Button label="Upload from computer" size="sm36" variant="ghost" disabled={shots.length >= MAX_SHOTS} onClick={() => fileInputRef.current?.click()} />
          </div>
          {nativeSupported && (
            <div style={styles.inlineBtn} title="Close this dialog, navigate anywhere, then capture from the floating button">
              <Button label="Add another screen" size="sm36" variant="ghost" disabled={shots.length >= MAX_SHOTS} onClick={onStash} />
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(event) => {
              appendShots(Array.from(event.target.files || []));
              event.target.value = "";
            }}
          />
        </div>
        <div style={styles.dropHint(dragDepth > 0)}>
          {dragDepth > 0 ? "Drop to attach" : "Tip: paste (Ctrl/Cmd+V) or drag-drop images anywhere on this dialog. A report with no screenshot is fine too."}
        </div>
      </div>
      <div style={styles.dialogFooter}>
        <div style={styles.inlineBtn}>
          <Button label="My reports" size="sm36" variant="text" onClick={() => { setStep("list"); loadMyIssues(); }} />
        </div>
        <span style={{ flex: 1 }} />
        {aiEnabled && (
          <div style={styles.inlineBtn}>
            <Button
              label={assistBusy ? `Polishing... ${assistSeconds}s` : "Polish with AI"}
              size="sm36"
              variant="ghost"
              disabled={!canFile || assistBusy}
              onClick={() => runPolish(null)}
            />
          </div>
        )}
        {aiEnabled && (
          <div style={styles.inlineBtn} title="Files instantly; AI patches the spec in the background">
            <Button
              label="File & polish in background"
              size="sm36"
              variant="ghost"
              disabled={!canFile}
              onClick={() => fileIssue({ withSpec: false, polishAsync: true })}
            />
          </div>
        )}
        <div style={styles.inlineBtn}>
          <Button
            label={fileBusy ? "Filing..." : "File report"}
            size="sm36"
            variant="primary"
            disabled={!canFile}
            onClick={() => fileIssue({ withSpec: false })}
          />
        </div>
      </div>
    </>
  );

  const renderClarify = () => (
    <>
      <div style={styles.dialogBody}>
        <TypographyInline size="sm" color={GREY.PRIMARY} label="One quick question to sharpen the report:" fontWeight={fontWeight.REGULAR} />
        <div style={{ marginTop: "4px", marginBottom: "8px" }}>
          <TypographyInline size="sm" color={GREY.PRIMARY} label={clarify.question} fontWeight={fontWeight.SEMIBOLD} />
        </div>
        <TextArea
          isResize
          autoFocus
          rows={4}
          placeholder="Your answer (optional - you can skip)"
          value={clarify.answer}
          onChange={(event) => setClarify((prev) => ({ ...prev, answer: event.target.value }))}
        />
      </div>
      <div style={styles.dialogFooter}>
        <div style={styles.inlineBtn}>
          <Button label="Back" size="sm36" variant="ghost" onClick={() => setStep("compose")} />
        </div>
        <div style={styles.inlineBtn}>
          <Button
            label={assistBusy ? `Polishing... ${assistSeconds}s` : "Continue"}
            size="sm36"
            variant="ghost"
            disabled={assistBusy}
            onClick={() => runPolish(clarify.answer.trim() || "(no answer provided)")}
          />
        </div>
      </div>
    </>
  );

  const specField = (label, key, multiline, placeholder) => (
    <>
      <FieldLabel label={label} />
      {multiline ? (
        <TextArea
          isResize
          rows={3}
          value={specForm[key]}
          placeholder={placeholder || ""}
          onChange={(event) => setSpecForm((prev) => ({ ...prev, [key]: event.target.value }))}
        />
      ) : (
        <InputField
          size="sm36"
          value={specForm[key]}
          placeholder={placeholder || ""}
          onChange={(event) => setSpecForm((prev) => ({ ...prev, [key]: event.target.value }))}
        />
      )}
    </>
  );

  const renderReview = () => (
    <>
      <div style={styles.dialogBody}>
        {aiSeeded && <NoteText label="AI draft - every field below is editable before filing." />}
        {specField("Title", "title", false, "Short, specific title")}
        {/* Type + Severity use the house AutoCompleteCustom (SearchField trigger + styled
            popover) instead of the raw native <select> - matches the rest of the app.
            align-items:flex-start so neither column stretches to the other's height. */}
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FieldLabel label="Type" />
            <AutoCompleteCustom
              size="sm36"
              dropdownId="qa-spec-kind"
              items={KIND_ITEMS}
              placeholder="Select type"
              valueState={kindValue}
              setValueState={() => {}}
              idState={[specForm.kind]}
              setIdState={() => {}}
              // disablePortal: render the menu INSIDE the QA dialog's portal so it (a)
              // inherits the dialog's z-index (1400) and isn't hidden behind it, and
              // (b) sits inside [data-qa-exclude] so the QA focus guard keeps it usable.
              disablePortal
              onChange={({ id }) => setSpecForm((p) => ({ ...p, kind: id?.[0] ?? p.kind }))}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FieldLabel label="Severity" />
            <AutoCompleteCustom
              size="sm36"
              dropdownId="qa-spec-severity"
              items={SEVERITY_ITEMS}
              placeholder="Select severity"
              valueState={severityValue}
              setValueState={() => {}}
              idState={[specForm.severity]}
              setIdState={() => {}}
              disablePortal
              onChange={({ id }) => setSpecForm((p) => ({ ...p, severity: id?.[0] ?? "" }))}
            />
          </div>
        </div>
        {/* severity_reason rendered full-width BELOW the row, never inside the narrow
            column where it used to overflow and collide with the Area field. */}
        {specForm.severity_reason && <NoteText label={specForm.severity_reason} />}
        {specField("Area", "area", false, "e.g. CRM > Contacts")}
        {specField("Expected", "expected", true)}
        {specField("Actual", "actual", true)}
        {specField("Repro steps (one per line, max 5)", "repro", true)}
        {specField("Acceptance criteria (one per line, max 3)", "acceptance", true)}
        {specField("Suggested fix direction", "fix_direction", true)}
        {specField("Impact (one sentence)", "impact", false)}
        {duplicates.length > 0 && (
          <div style={styles.banner}>
            <TypographyInline size="xs" color={GREY.PRIMARY} label="Possible duplicates:" fontWeight={fontWeight.SEMIBOLD} />
            {duplicates.map((dup) => (
              <div key={dup.id} style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ flex: 1 }}>
                  <TypographyInline size="xs" color={GREY.PRIMARY} label={dup.reason || dup.id} fontWeight={fontWeight.REGULAR} />
                </span>
                <div style={styles.inlineBtn}>
                  <Button label="+1 instead" size="sm36" variant="text" onClick={() => plusOneDuplicate(dup.id)} />
                </div>
              </div>
            ))}
          </div>
        )}
        {shots.length > 0 && renderShotsStrip()}
      </div>
      <div style={styles.dialogFooter}>
        <div style={styles.inlineBtn}>
          <Button label="Back" size="sm36" variant="ghost" onClick={() => setStep("compose")} />
        </div>
        <div style={styles.inlineBtn}>
          <Button
            label={fileBusy ? "Filing..." : "File report"}
            size="sm36"
            variant="primary"
            disabled={fileBusy}
            onClick={() => fileIssue({ withSpec: true })}
          />
        </div>
      </div>
    </>
  );

  const renderDone = () => (
    <>
      <div style={{ ...styles.dialogBody, textAlign: "center", padding: "32px 18px" }}>
        <div style={{ fontSize: fontSize.DSM, color: SUCCESS.W_500, lineHeight: 1 }}>&#10003;</div>
        <div style={{ margin: "8px 0" }}>
          <TypographyInline size="md" color={GREY.PRIMARY} label={`Report #${filed?.seq} filed. Thank you!`} fontWeight={fontWeight.SEMIBOLD} />
        </div>
        {filed?.polish_async && <NoteText label="AI is polishing the spec in the background - your report is already in the tracker." />}
        <NoteText label="You'll see a badge on the Report button when a fix is ready for your confirmation." />
      </div>
      <div style={styles.dialogFooter}>
        <div style={styles.inlineBtn}>
          <Button label="File another" size="sm36" variant="ghost" onClick={resetAll} />
        </div>
        <div style={styles.inlineBtn}>
          <Button label="Close" size="sm36" variant="primary" onClick={() => { resetAll(); onClose(); }} />
        </div>
      </div>
    </>
  );

  const renderList = () => (
    <>
      <div style={styles.dialogBody}>
        {myIssues === null && <NoteText label="Loading..." />}
        {Array.isArray(myIssues) && myIssues.length === 0 && <NoteText label="No reports yet - file your first one!" />}
        {Array.isArray(myIssues) && myIssues.map((issue) => {
          const meta = STATUS_META[issue.status] || STATUS_META.open;
          return (
            <div key={issue._id} style={styles.listRow} onClick={() => openDetail(issue._id)}>
              <div style={{ minWidth: 0 }}>
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  <TypographyInline size="sm" color={GREY.PRIMARY} label={`#${issue.seq} ${issue.title}`} fontWeight={fontWeight.SEMIBOLD} isEllipses />
                </div>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label={`${issue.kind} - ${formatDate(issue.created_at)}${issue.severity ? ` - ${issue.severity}` : ""}`}
                  fontWeight={fontWeight.REGULAR}
                />
              </div>
              <StatusChip label={meta.label} variant={meta.variant} />
            </div>
          );
        })}
      </div>
      <div style={styles.dialogFooter}>
        <div style={styles.inlineBtn}>
          <Button label="Back" size="sm36" variant="ghost" onClick={() => setStep("compose")} />
        </div>
      </div>
    </>
  );

  const renderDetail = () => {
    const issue = detail?.issue;
    const events = detail?.events || [];
    if (!issue) {
      return <div style={styles.dialogBody}><NoteText label="Loading..." /></div>;
    }
    const meta = STATUS_META[issue.status] || STATUS_META.open;
    const where = [issue.context?.section, issue.context?.tab].filter(Boolean).join(" > ");
    return (
      <>
        <div style={styles.dialogBody}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ flex: 1 }}>
              <TypographyInline size="sm" color={GREY.PRIMARY} label={`#${issue.seq} ${issue.title}`} fontWeight={fontWeight.SEMIBOLD} />
            </span>
            <StatusChip label={meta.label} variant={meta.variant} />
          </div>
          {where && <NoteText style={{ marginTop: "4px" }} label={`Where: ${where}${issue.context?.env ? ` (${issue.context.env})` : ""}`} />}
          {issue.status === "fixed" && (
            <div style={styles.banner}>
              <TypographyInline size="xs" color={GREY.PRIMARY} label="A fix is ready for your confirmation." fontWeight={fontWeight.SEMIBOLD} />
              {issue.fix_note && (
                <div style={{ marginTop: "4px" }}>
                  <TypographyInline size="xs" color={GREY.PRIMARY} label={`Fix note: ${issue.fix_note}${issue.fixed_build ? ` (build ${issue.fixed_build})` : ""}`} fontWeight={fontWeight.REGULAR} />
                </div>
              )}
              {verifyMode !== "no" ? (
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <div style={styles.inlineBtn}>
                    <Button label="Yes, it works" size="sm36" variant="primary" onClick={() => submitVerify(true)} />
                  </div>
                  <div style={styles.inlineBtn}>
                    <Button label="Still broken" size="sm36" variant="ghost" onClick={() => setVerifyMode("no")} />
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: "8px" }}>
                  <TextArea
                    isResize
                    autoFocus
                    rows={2}
                    placeholder="What is still broken? (required)"
                    value={verifyNote}
                    onChange={(event) => setVerifyNote(event.target.value)}
                  />
                  <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                    <div style={styles.inlineBtn}>
                      <Button label="Cancel" size="sm36" variant="ghost" onClick={() => setVerifyMode(null)} />
                    </div>
                    <div style={styles.inlineBtn}>
                      <Button label="Reopen issue" size="sm36" variant="primary" onClick={() => submitVerify(false)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <FieldLabel label="Description" />
          <div style={{ whiteSpace: "pre-wrap", margin: "2px 0" }}>
            <TypographyInline size="sm" color={GREY.QUATINARY} label={issue.description} fontWeight={fontWeight.REGULAR} />
          </div>
          {issue.spec?.impact && <NoteText label={`Impact: ${issue.spec.impact}`} />}
          {shotCount > 0 && (
            <>
              <FieldLabel label="Screenshots" />
              <div style={styles.shotsStrip}>
                {shotsLoading
                  ? Array.from({ length: shotCount }).map((_, index) => (
                      <div key={index} style={styles.shotSkeleton}>
                        <span style={styles.shotSpinner} />
                      </div>
                    ))
                  : detailShots.map((url, index) => (
                      <a key={index} href={url} target="_blank" rel="noreferrer" style={styles.shotThumbWrap}>
                        <img src={url} alt={`screenshot ${index + 1}`} style={styles.shotThumb} />
                      </a>
                    ))}
              </div>
            </>
          )}
          <FieldLabel label="Timeline" />
          <ul style={styles.timeline}>
            {events.map((event, index) => {
              const detailLine = describeEvent(event);
              const isLast = index === events.length - 1;
              return (
                <li key={index} style={styles.timelineItem}>
                  <span style={styles.timelineMarker}>
                    <span style={styles.timelineDot} />
                    {!isLast && <span style={styles.timelineConnector} />}
                  </span>
                  <div style={styles.timelineContent}>
                    <strong>{eventLabel(event)}</strong> {formatDate(event.created_at)} - {event.actor_email}
                    {detailLine ? <div style={{ whiteSpace: "pre-line", marginTop: "2px" }}>{detailLine}</div> : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <div style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "stretch" }}>
            <div style={{ flex: 1 }}>
              <InputField
                size="sm36"
                placeholder="Add a comment..."
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && submitComment()}
              />
            </div>
            <div style={styles.inlineBtn}>
              <Button label="Send" size="sm36" variant="ghost" onClick={submitComment} />
            </div>
          </div>
        </div>
        <div style={styles.dialogFooter}>
          <div style={styles.inlineBtn}>
            <Button label="Back" size="sm36" variant="ghost" onClick={() => { cleanupDetailShots(); setDetailShots([]); setStep("list"); loadMyIssues(); }} />
          </div>
        </div>
      </>
    );
  };

  const titles = {
    compose: draft.kind === "feature" ? "Request a feature" : "Report a bug",
    clarify: "One quick question",
    review: "Review & file",
    done: "Filed",
    list: "My reports",
    detail: "Report detail",
  };

  return (
    <>
      <QaPortalModal open={open}>
        <div style={styles.dialogHeader}>
          <TypographyInline size="md" color={BASE.WHITE} label={titles[step]} fontWeight={fontWeight.SEMIBOLD} />
          <div style={styles.closeIconContainer}>
            <IconComponent
              id={"close_button"}
              iconLabel={ICON_LABELS.CLOSE_BUTTON}
              style={styles.closeIconStyle}
              onClick={onClose}
            />
          </div>
        </div>
        {step === "compose" && renderCompose()}
        {step === "clarify" && renderClarify()}
        {step === "review" && renderReview()}
        {step === "done" && renderDone()}
        {step === "list" && renderList()}
        {step === "detail" && renderDetail()}
      </QaPortalModal>
      {annotateIndex !== null && shots[annotateIndex] && (
        <AnnotateShot
          shot={shots[annotateIndex]}
          onCancel={() => setAnnotateIndex(null)}
          onSave={(updated) => {
            setDraft((prev) => ({ ...prev, shots: prev.shots.map((s, i) => (i === annotateIndex ? updated : s)) }));
            setAnnotateIndex(null);
          }}
        />
      )}
    </>
  );
};

export default ReportModal;
